import {
	AfterContentInit,
	Component,
	Input,
	forwardRef,
	ContentChildren,
	QueryList,
	OnDestroy
} from '@angular/core';

import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { BaseControlValueAccessor } from 'rev-shared/util/BaseControlValueAccessor';

import { VbRadioBtnComponent } from './VbRadioBtn.Component';

/**
 * Radio Button common component.
 * usage -
 * <vb-radio-group-btn>
		<vb-radio-btn
			[name]="'radio'"
			[vbUiRadioBtn]="'radio1'"
			[(ngModel)]="buttonValue">
			Radio 1
		</vb-radio-btn>
		<vb-radio-btn
			[name]="'radio'"
			[vbUiRadioBtn]="'radio2'"
			[(ngModel)]="buttonValue">
			Radio 2
		</vb-radio-btn>
 * </vb-radio-group-btn>
 */
@Component({
	selector: 'vb-radio-group-btn',
	template: '<ng-content></ng-content>',
	host: {
		'role': 'radiogroup',
		'class': 'box-block'
	},
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => VbRadioGroupBtnComponent),
			multi: true
		}
	],
	standalone: false
})
export class VbRadioGroupBtnComponent extends BaseControlValueAccessor<string> implements AfterContentInit, OnDestroy {
	@ContentChildren(forwardRef(() => VbRadioBtnComponent), { descendants: true })
	private readonly radios: QueryList<VbRadioBtnComponent>;

	private selectedRadio: VbRadioBtnComponent;
	private radiosChangesSubscription: { unsubscribe: () => void };

	public ngAfterContentInit(): void {
		setTimeout(() => this.syncSelectedFromValue());

		this.radiosChangesSubscription = this.radios?.changes?.subscribe(() => {
			this.syncSelectedFromValue();
		});
	}

	public ngOnDestroy(): void {
		this.radiosChangesSubscription?.unsubscribe();
	}

	public override writeValue(val: string): void {
		super.writeValue(val);
		this.syncSelectedFromValue();
	}

	public onOptionSelect(radio: VbRadioBtnComponent) {
		if (this.selectedRadio === radio) {
			return;
		}
		this.resetAllRadios();

		this.selectedRadio = radio;
		this.selectedRadio.checked = true;
		this.updateModelValue(this.selectedRadio.optionValue);
		this.selectedRadio.changed.emit(radio);
	}

	private resetAllRadios(): void {
		this.radios.forEach(radio => radio.checked = false);
	}

	private syncSelectedFromValue(): void {
		if (!this.radios?.length) {
			return;
		}

		this.resetAllRadios();
		this.selectedRadio = this.radios.find(radio => radio.optionValue === this.value);
		if (this.selectedRadio) {
			this.selectedRadio.checked = true;
		}
	}
}
